import { instantDeSac } from "./types-de-champ.js";
import { sansTypeFondu } from "./catalogue-des-types.mjs";
import { AUTEUR_LIA, AUTEUR_SYSTEME, EVENEMENT_FICHE_LIEE_CREEE, EVENEMENT_FICHE_MODIFIEE, SUIVI_CHANGEMENTS_MAX, SUIVI_VALEUR_MAX } from "./message.js";

/**
 * LE JOURNAL DU SUIVI EN MOTS : UNE SEULE REGLE, PARTOUT (lot Q du chantier
 * `fiche-suivi`, 27/10/2026). Le fondateur ne doit JAMAIS lire une clef
 * technique : le volet de la fiche disait « Étape : À faire -> En cours »,
 * mais le centre de communication montrait le meme message
 * « Status : aFaire -> enCours », parce que le serveur ecrivait la clef rendue
 * lisible et la valeur brute, et que seul le volet les remettait en mots.
 *
 * DESORMAIS, une seule fonction met un changement en mots, a partir du
 * CATALOGUE de la declaration (le libelle du champ, celui de ses options, son
 * type), et tous l'appellent :
 *  - le SERVEUR (`functions/src/chatter/`), qui l'embarque par
 *    `functions/build-studio.mjs`, ecrit `systeme.changements` et le texte
 *    deja en mots, depuis la declaration effective de l'org : le MCP, Lia et
 *    le centre lisent des mots ;
 *  - le VOLET (`packages/app-runtime/src/suivi.ts`) et le CENTRE
 *    (`packages/shell/src/centre/`) remettent en mots les ANCIENS messages,
 *    ecrits avant avec des clefs, par le catalogue de la fiche. Sur un message
 *    neuf, la fonction ne change rien : un libelle d'option n'est pas une
 *    valeur d'option, une date en mots n'est pas une date `AAAA-MM-JJ`.
 *
 * Module PUR (ni Firestore, ni React, ni fichier) : le serveur, l'ecran et les
 * tests le lisent tel quel. Contrat : `docs/socle/chatter.md`, section 5.
 */

/** Les reglages d'un champ declare qui changent son texte (devise, unite, jauge, plusieurs, note sur). */
export interface ReglagesDuCatalogue {
  unite?: string;
  devise?: string;
  decimales?: number;
  affichage?: string;
  plusieurs?: boolean;
  max?: number;
}

/** Ce que la declaration sait d'un champ : son libelle, son type, le libelle de chaque option, ses reglages. */
export interface ChampDuCatalogue {
  libelle: string;
  /** Le type declare (`select`, `date`, `personne`...), absent s'il n'est pas dit. */
  type?: string;
  /** Valeur stockee -> libelle, pour un champ a options. */
  options?: ReadonlyMap<string, string>;
  /** Les reglages d'un champ DECLARE (`taki.fields.json`), absents pour un natif. */
  reglages?: ReglagesDuCatalogue;
}

/**
 * LE CATALOGUE D'UN JEU, par CHEMIN DE STOCKAGE : `status` pour un natif
 * (`socle:status` dans le vocabulaire des colonnes), `customFields.categorie`
 * pour un champ declare. Le chemin est ce que le serveur ecrit dans
 * `systeme.changements[].champ`.
 */
export type CatalogueDuSuivi = ReadonlyMap<string, ChampDuCatalogue>;

/**
 * LA REGLE DE TEXTE D'UNE VALEUR PAR SON TYPE, celle de la case de fiche et du
 * PDF (`texteDeValeur` de `@taki/ui`, chantier `core/champs-types`) : « 1 250,50 € »,
 * « 65 % », « 2 sur 3 », « 4 / 5 ». Une liste lui arrive une valeur par ligne.
 * `undefined` : le type n'a pas de mot a lui, la regle du journal reprend.
 * Le data-model ne lit pas `@taki/ui` : qui l'a la passe (le serveur, par
 * `functions/build-studio.mjs`).
 */
export type TexteParType = (
  type: string,
  valeur: string | number | boolean | Date,
  reglages: ReglagesDuCatalogue,
  options?: readonly { value: string; label: string }[],
) => string | undefined;

/**
 * Ce que la mise en mots sait en plus du catalogue : le nom d'une personne par
 * son personId, et la regle de texte par type. Le serveur passe les deux ; le
 * volet et le centre ne remettent en mots que d'anciens messages, deja ecrits,
 * et ne passent pas la regle par type.
 */
export interface ContexteDesMots {
  nomDePersonne?: (personId: string) => string | null | undefined;
  texteParType?: TexteParType;
}

type Objet = Record<string, unknown>;
const estObjet = (valeur: unknown): valeur is Objet => valeur !== null && typeof valeur === "object" && !Array.isArray(valeur);
const tableau = (valeur: unknown): unknown[] => (Array.isArray(valeur) ? valeur : []);
const texteNonVide = (valeur: unknown): valeur is string => typeof valeur === "string" && valeur.trim() !== "";

/** Un jeu couvre une fiche : meme famille, et meme type quand les deux en ont un (`reglage.ts` du serveur dit pareil). */
function couvre(jeu: unknown, family: string, type: string | null): boolean {
  if (!estObjet(jeu) || jeu["family"] !== family) return false;
  const sien = typeof jeu["type"] === "string" ? jeu["type"] : null;
  return type === null || sien === null || sien === type;
}

function optionsDe(brutes: unknown): ReadonlyMap<string, string> | undefined {
  const options = new Map<string, string>();
  for (const option of tableau(brutes)) {
    if (!estObjet(option) || typeof option["value"] !== "string") continue;
    options.set(option["value"], texteNonVide(option["label"]) ? option["label"].trim() : option["value"]);
  }
  return options.size === 0 ? undefined : options;
}

/** Les reglages qui changent le texte d'une valeur, lus sur un champ declare. */
function reglagesDe(champ: Objet): ReglagesDuCatalogue | undefined {
  const reglages: ReglagesDuCatalogue = {
    ...(texteNonVide(champ["unite"]) ? { unite: champ["unite"] } : {}),
    ...(texteNonVide(champ["devise"]) ? { devise: champ["devise"] } : {}),
    ...(typeof champ["decimales"] === "number" ? { decimales: champ["decimales"] } : {}),
    ...(texteNonVide(champ["affichage"]) ? { affichage: champ["affichage"] } : {}),
    ...(typeof champ["plusieurs"] === "boolean" ? { plusieurs: champ["plusieurs"] } : {}),
    ...(typeof champ["max"] === "number" ? { max: champ["max"] } : {}),
  };
  return Object.keys(reglages).length === 0 ? undefined : reglages;
}

function champDuCatalogue(libelle: unknown, type: unknown, options: unknown, repli: string, reglages?: ReglagesDuCatalogue): ChampDuCatalogue {
  const lues = optionsDe(options);
  return {
    libelle: texteNonVide(libelle) ? libelle.trim() : repli,
    ...(typeof type === "string" && type !== "" ? { type } : {}),
    ...(lues === undefined ? {} : { options: lues }),
    ...(reglages === undefined ? {} : { reglages }),
  };
}

/** Les ecrans d'une declaration : `{ ecrans: { ecrans: [...] } }` (fichier, instance), ou `{ ecran }` (l'ecran monte). */
function ecransDe(declaration: Objet): Objet[] {
  const fichier = declaration["ecrans"];
  if (estObjet(fichier)) return tableau(fichier["ecrans"]).filter(estObjet);
  return estObjet(declaration["ecran"]) ? [declaration["ecran"]] : [];
}

/**
 * LE CATALOGUE D'UNE FICHE (famille et type) dans une ou plusieurs
 * declarations (`{ champs, ecrans }`, celle de l'org : instance sinon modele).
 * Les champs DECLARES viennent du jeu qui couvre la fiche (`taki.fields.json`) ;
 * les champs NATIFS, des colonnes `socle:<champ>` des ecrans qui la montrent
 * (celles de l'ecran, puis celles de ses portes, qui l'emportent). Plusieurs
 * declarations (deux apps montrent le meme jeu) : la premiere qui connait un
 * champ le nomme.
 */
export function catalogueDuSuivi(
  declarations: readonly unknown[],
  jeu: { family: string; type?: string | null | undefined },
): CatalogueDuSuivi {
  const type = typeof jeu.type === "string" && jeu.type !== "" ? jeu.type : null;
  const catalogue = new Map<string, ChampDuCatalogue>();
  for (const declaration of declarations) {
    if (!estObjet(declaration)) continue;
    /* Un type fondu (un ancien `montant`) se relit comme le reste du socle
       (`sansTypeFondu`) : `number` et sa devise. */
    const relus = sansTypeFondu(declaration["champs"]);
    const champs = estObjet(relus) ? tableau(relus["jeux"]) : [];
    for (const declare of champs.filter((candidat) => couvre(candidat, jeu.family, type))) {
      for (const champ of tableau((declare as Objet)["champs"])) {
        if (!estObjet(champ) || !texteNonVide(champ["key"])) continue;
        const chemin = `customFields.${champ["key"]}`;
        if (!catalogue.has(chemin)) catalogue.set(chemin, champDuCatalogue(champ["label"], champ["type"], champ["options"], libelleLisible(chemin), reglagesDe(champ)));
      }
    }
    for (const ecran of ecransDe(declaration)) {
      const portes = tableau(ecran["portes"]).filter((porte): porte is Objet => estObjet(porte) && couvre(porte["jeu"], jeu.family, type));
      if (portes.length === 0) continue;
      const natifs = new Map<string, ChampDuCatalogue>();
      for (const colonne of [...tableau(ecran["colonnes"]), ...portes.flatMap((porte) => tableau(porte["colonnes"]))]) {
        if (!estObjet(colonne) || typeof colonne["champ"] !== "string" || !colonne["champ"].startsWith("socle:")) continue;
        const chemin = colonne["champ"].slice("socle:".length);
        if (chemin === "") continue;
        const precedent = natifs.get(chemin);
        const lu = champDuCatalogue(colonne["label"], colonne["type"], colonne["options"], precedent?.libelle ?? libelleLisible(chemin));
        natifs.set(chemin, {
          ...precedent,
          ...lu,
          ...(lu.options === undefined && precedent?.options !== undefined ? { options: precedent.options } : {}),
        });
      }
      for (const [chemin, champ] of natifs) if (!catalogue.has(chemin)) catalogue.set(chemin, champ);
    }
  }
  return catalogue;
}

/**
 * LE REPLI : une clef rendue lisible, quand aucun catalogue ne connait le
 * champ (`customFields.dateDeDebut` -> « Date de debut »). Jamais le chemin tel
 * quel.
 */
export function libelleLisible(champ: string): string {
  const brut = champ.startsWith("customFields.") ? champ.slice("customFields.".length) : champ;
  const mots = brut
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return mots.length === 0 ? brut : mots.charAt(0).toUpperCase() + mots.slice(1);
}

const coupe = (texte: string, max: number): string => (texte.length > max ? `${texte.slice(0, max - 1)}…` : texte);
const DATE_PURE = /^(\d{4})-(\d{2})-(\d{2})$/;
const FORMAT_DE_DATE = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeZone: "UTC" });

/** Une date en mots (« 27 oct. 2026 ») ; un instant se dit par son jour, comme avant. */
function dateEnMots(date: Date): string | null {
  return Number.isNaN(date.getTime()) ? null : FORMAT_DE_DATE.format(date);
}

function instantDe(valeur: unknown): Date | null {
  if (valeur instanceof Date) return valeur;
  if (estObjet(valeur) && typeof valeur["toDate"] === "function") {
    try {
      const date = (valeur["toDate"] as () => unknown).call(valeur);
      return date instanceof Date ? date : null;
    } catch {
      return null;
    }
  }
  return null;
}

const A_DATE = new Set(["date", "datetime"]);

/** Une valeur simple en mots : son option, son nom de personne, sa date, son nombre. */
function uneValeur(valeur: unknown, champ: ChampDuCatalogue | undefined, contexte: ContexteDesMots): string {
  if (typeof valeur === "boolean") return valeur ? "oui" : "non";
  if (typeof valeur === "number") {
    return Number.isInteger(valeur) ? String(valeur) : valeur.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
  }
  const instant = instantDe(valeur);
  if (instant !== null) return dateEnMots(instant) ?? "(date)";
  if (typeof valeur === "string") {
    const option = champ?.options?.get(valeur);
    if (option !== undefined) return option;
    if (champ?.type === "personne") {
      const nom = contexte.nomDePersonne?.(valeur);
      if (texteNonVide(nom)) return nom.trim();
    }
    /* Une date-heure du sac est rangee en texte ISO (`instantDeSac`,
       `docs/socle/datetime-sac.md`) : elle se dit par son jour, comme un instant natif. */
    const duSac = champ?.type === "datetime" ? instantDeSac(valeur) : null;
    if (duSac !== null) return dateEnMots(duSac) ?? valeur;
    const jour = DATE_PURE.exec(valeur);
    if (jour !== null && champ?.type !== undefined && A_DATE.has(champ.type)) {
      return dateEnMots(new Date(Date.UTC(Number(jour[1]), Number(jour[2]) - 1, Number(jour[3])))) ?? valeur;
    }
    return valeur;
  }
  return "(objet)";
}

/**
 * LA VALEUR STOCKEE D'UN CHAMP TYPE, COMME LA FICHE L'ECRIT, quand le contexte
 * porte la regle par type (`texteParType`) : une liste passe une valeur par
 * ligne, comme a l'ecran. `undefined` : pas de type, pas de regle, une valeur
 * objet (hors date), ou un type sans mot a lui.
 */
function valeurParType(valeur: unknown, champ: ChampDuCatalogue | undefined, contexte: ContexteDesMots): string | undefined {
  if (contexte.texteParType === undefined || champ?.type === undefined) return undefined;
  const cellule = Array.isArray(valeur) ? valeur.map(String).join("\n") : (instantDe(valeur) ?? valeur);
  if (typeof cellule !== "string" && typeof cellule !== "number" && typeof cellule !== "boolean" && !(cellule instanceof Date)) return undefined;
  const options = champ.options === undefined ? undefined : [...champ.options].map(([value, label]) => ({ value, label }));
  return contexte.texteParType(champ.type, cellule, champ.reglages ?? {}, options);
}

/**
 * UNE VALEUR D'UN CHANGEMENT EN MOTS, ou `null` pour vide (l'ecran dit « vide »
 * a sa facon, le texte dit « (vide) »). Brute (le serveur : chaine, nombre,
 * booleen, date, liste) ou deja ecrite (un ancien message : une chaine, une
 * liste jointe par des virgules) : la meme regle. Trois elements d'une liste au
 * plus, le reste compte ; `max` signes au plus.
 */
export function valeurDuSuivi(
  valeur: unknown,
  champ?: ChampDuCatalogue,
  contexte: ContexteDesMots = {},
  max: number = SUIVI_VALEUR_MAX,
): string | null {
  if (valeur === null || valeur === undefined || valeur === "") return null;
  const typee = valeurParType(valeur, champ, contexte);
  if (typee !== undefined) return typee === "" ? null : coupe(typee, max);
  let liste: unknown[] | null = Array.isArray(valeur) ? valeur : null;
  /* UN ANCIEN MESSAGE d'une liste a choix multiples porte « interne, externe » :
     chaque morceau qui est une option se remet en mots. */
  if (liste === null && typeof valeur === "string" && champ?.options !== undefined && champ.options.get(valeur) === undefined && valeur.includes(", ")) {
    const morceaux = valeur.split(", ");
    if (morceaux.every((morceau) => champ.options?.has(morceau))) liste = morceaux;
  }
  if (liste !== null) {
    if (liste.length === 0) return null;
    const mots = liste.slice(0, 3).map((element) => uneValeur(element, champ, contexte));
    return coupe(liste.length > 3 ? `${mots.join(", ")} (+${liste.length - 3})` : mots.join(", "), max);
  }
  return coupe(uneValeur(valeur, champ, contexte), max);
}

/** Un changement tel que le suivi le garde : le chemin, le libelle, avant, apres, en mots. */
export interface ChangementDuSuiviEnMots {
  champ: string;
  libelle: string;
  avant: string | null;
  apres: string | null;
}

/**
 * UN CHANGEMENT EN MOTS, LA SEULE REGLE. `libelle` : celui du catalogue, sinon
 * celui deja ecrit (un ancien message), sinon la clef rendue lisible. Les
 * valeurs : `valeurDuSuivi`. Idempotente : un changement deja en mots ressort
 * tel quel.
 */
export function changementDuSuivi(
  changement: { champ: string; libelle?: string | null | undefined; avant: unknown; apres: unknown },
  catalogue: CatalogueDuSuivi,
  contexte: ContexteDesMots = {},
): ChangementDuSuiviEnMots {
  const champ = catalogue.get(changement.champ);
  const libelle = champ?.libelle ?? (texteNonVide(changement.libelle) ? changement.libelle.trim() : libelleLisible(changement.champ));
  return {
    champ: coupe(changement.champ, SUIVI_VALEUR_MAX),
    libelle: coupe(libelle, SUIVI_VALEUR_MAX),
    avant: valeurDuSuivi(changement.avant, champ, contexte),
    apres: valeurDuSuivi(changement.apres, champ, contexte),
  };
}

/** Plafond d'une valeur dans le TEXTE d'un changement (la structure garde jusqu'a `SUIVI_VALEUR_MAX`). */
const VALEUR_DU_TEXTE_MAX = 60;
const dansLeTexte = (valeur: string | null): string => (valeur === null ? "(vide)" : coupe(valeur, VALEUR_DU_TEXTE_MAX));

/**
 * LE TEXTE D'UN CHANGEMENT, depuis les changements EN MOTS : « Étape : À faire
 * -> En cours, par Rita » pour un champ ; « 3 champs modifiés par Rita : ... »
 * pour plusieurs, vingt au plus, le reste compte (`total`, le nombre de champs
 * changes, quand il depasse la liste). C'est ce texte que lisent le MCP, Lia
 * et le fil du centre.
 */
export function texteDesChangementsDuSuivi(
  changements: readonly Pick<ChangementDuSuiviEnMots, "libelle" | "avant" | "apres">[],
  auteur: string | null,
  total: number = changements.length,
): string {
  const lignes = changements.slice(0, SUIVI_CHANGEMENTS_MAX).map((c) => `${c.libelle} : ${dansLeTexte(c.avant)} -> ${dansLeTexte(c.apres)}`);
  const nombre = Math.max(total, changements.length);
  const qui = texteNonVide(auteur) ? auteur.trim() : null;
  if (nombre === 1) return `${lignes[0] ?? ""}${qui === null ? "" : `, par ${qui}`}`;
  const manquants = nombre - lignes.length;
  const suite = manquants > 0 ? ` ; et ${manquants} ${manquants > 1 ? "autres champs" : "autre champ"}` : "";
  return `${nombre} champs modifiés${qui === null ? "" : ` par ${qui}`} : ${lignes.join(" ; ")}${suite}`;
}

/** Un jeu d'un lot, vu de qui le dit : sa porte (`null` si le catalogue ne la donne pas) et son compte. */
export interface JeuDuLotEnMots {
  libelle: string | null | undefined;
  nombre: number;
}

/**
 * LE COMPTE PAR JEU d'un lot lu tel qu'il est stocke (`systeme.lot`), dans
 * l'ordre ou chaque jeu est entre dans le lot. Un ANCIEN lot, d'un seul jeu
 * (sans `jeux`), se lit par `libelle` + `nombre` : rien n'est migre. Un lot
 * mal forme donne une liste vide.
 */
export function libellesDuLot(lot: unknown): JeuDuLotEnMots[] {
  if (!estObjet(lot)) return [];
  const jeux = tableau(lot["jeux"]).filter((j): j is Objet => estObjet(j) && typeof j["nombre"] === "number" && j["nombre"] >= 1);
  if (jeux.length > 0) return jeux.map((j) => ({ libelle: texteNonVide(j["libelle"]) ? j["libelle"].trim() : null, nombre: j["nombre"] as number }));
  const nombre = lot["nombre"];
  return typeof nombre === "number" ? [{ libelle: texteNonVide(lot["libelle"]) ? lot["libelle"].trim() : null, nombre }] : [];
}

/**
 * LA PARENTHESE D'UN LOT : « (Soins) » pour un seul jeu, « (Consultations 9,
 * Soins 12, Examens cliniques 8) » pour plusieurs, dans l'ordre d'entree des
 * jeux dans le lot. Deux jeux qui se disent pareil s'additionnent ; les jeux
 * sans porte se regroupent en « autres N » a la fin ; sans aucune porte, rien.
 */
function parentheseDuLot(jeux: string | null | undefined | readonly JeuDuLotEnMots[]): string {
  if (typeof jeux === "string" || jeux == null) return texteNonVide(jeux) ? ` (${jeux.trim()})` : "";
  const comptes = new Map<string, number>();
  let sansPorte = 0;
  for (const jeu of jeux) {
    if (texteNonVide(jeu.libelle)) comptes.set(jeu.libelle.trim(), (comptes.get(jeu.libelle.trim()) ?? 0) + jeu.nombre);
    else sansPorte += jeu.nombre;
  }
  if (comptes.size === 0) return "";
  if (comptes.size === 1 && sansPorte === 0) return ` (${[...comptes.keys()][0]})`;
  const morceaux = [...comptes].map(([libelle, nombre]) => `${libelle} ${nombre}`);
  if (sansPorte > 0) morceaux.push(`autres ${sansPorte}`);
  return ` (${morceaux.join(", ")})`;
}

/**
 * LE TEXTE D'UN LOT DE FICHES LIEES CREEES D'AFFILEE (`systeme.lot`, chantier
 * `suivi-regroupe`) : « 12 fiches liées créées (Soins), par Célia », ou, quand
 * le lot touche plusieurs jeux, « 36 fiches liées créées (Consultations 9,
 * Soins 12, Examens cliniques 8, Constantes 7), par Célia ». Le serveur
 * l'ecrit dans `texte` a chaque fiche ajoutee au lot (le MCP, Lia et le centre
 * le lisent tel quel), et le volet en tire sa phrase. Le deuxieme argument est
 * la porte d'un seul jeu (texte), ou le compte par jeu (`libellesDuLot`).
 * Pour UNE fiche, le serveur dit la fiche (« Fiche liée créée : D-12 (Factures),
 * par Taki », `evenementDeFicheLiee`) : cette fonction ne sert qu'a partir de deux.
 */
export function texteDesFichesLiees(
  nombre: number,
  jeux: string | null | undefined | readonly JeuDuLotEnMots[],
  auteur: string | null | undefined,
): string {
  const qui = texteNonVide(auteur) ? `, par ${auteur.trim()}` : "";
  return `${nombre} fiches liées créées${parentheseDuLot(jeux)}${qui}`;
}

/**
 * LA PHRASE D'UN LOT, apres le nom de qui l'a cree (le volet pose la bulle et
 * le nom devant) : « a créé 12 fiches liées (Soins) », « a créé 36 fiches
 * liées (Consultations 9, Soins 12, ...) ». `null` quand l'evenement n'est pas
 * un lot de deux fiches ou plus : l'appelant garde alors le texte.
 */
export function phraseDesFichesLiees(systeme: { evenement?: unknown; lot?: unknown } | null | undefined): string | null {
  if (systeme == null || systeme.evenement !== EVENEMENT_FICHE_LIEE_CREEE || !estObjet(systeme.lot)) return null;
  const nombre = systeme.lot["nombre"];
  if (typeof nombre !== "number" || !Number.isInteger(nombre) || nombre < 2) return null;
  return `a créé ${nombre} fiches liées${parentheseDuLot(libellesDuLot(systeme.lot))}`;
}

/** Le nombre de champs qu'un texte de changement annonce (« 25 champs modifiés »), pour un message relu. */
function totalAnnonce(texte: string): number {
  const lu = /^(\d+) champs modifiés/.exec(texte);
  return lu === null ? 0 : Number(lu[1]);
}

/**
 * LE TEXTE D'UN MESSAGE DU SUIVI EN MOTS, pour qui le montre sans le dessiner
 * (le fil du centre de communication). Un changement structure
 * (`fiche.modifiee` avec `changements`) se reecrit par la regle ci-dessus,
 * avec le nom de qui l'a fait (`systeme.par`) ; tout autre message garde son
 * texte (un ancien evenement sans structure, une creation, un message ecrit).
 */
export function texteDuMessageDuSuivi(
  message: { texte?: string | undefined; body?: string | undefined; systeme?: unknown },
  catalogue: CatalogueDuSuivi,
  contexte: ContexteDesMots & { nomDeQui?: (par: string) => string | null | undefined } = {},
): string {
  const texte = message.texte ?? message.body ?? "";
  const systeme = estObjet(message.systeme) ? message.systeme : null;
  if (systeme === null) return texte;
  /* UN LOT DE FICHES LIEES (deux ou plus) se redit par son compte, avec le nom
     de qui l'a cree : le centre et Canaux lisent la meme phrase que le serveur. */
  if (systeme["evenement"] === EVENEMENT_FICHE_LIEE_CREEE) {
    const lot = estObjet(systeme["lot"]) ? systeme["lot"] : null;
    const nombre = lot?.["nombre"];
    if (lot === null || typeof nombre !== "number" || !Number.isInteger(nombre) || nombre < 2) return texte;
    const par = typeof systeme["par"] === "string" ? systeme["par"] : null;
    const qui = par === null ? null
      : (contexte.nomDeQui?.(par) ?? (par === AUTEUR_LIA ? "Lia" : par === AUTEUR_SYSTEME ? "le système" : null));
    return texteDesFichesLiees(nombre, libellesDuLot(lot), qui);
  }
  if (systeme["evenement"] !== EVENEMENT_FICHE_MODIFIEE) return texte;
  const changements = tableau(systeme["changements"]).filter(
    (c): c is { champ: string; libelle?: string; avant: unknown; apres: unknown } => estObjet(c) && texteNonVide(c["champ"]),
  );
  if (changements.length === 0) return texte;
  const par = typeof systeme["par"] === "string" ? systeme["par"] : null;
  /* Lia et le systeme ont leur nom, comme dans le texte qu'ecrit le serveur. */
  const qui = par === null ? null
    : (contexte.nomDeQui?.(par) ?? (par === AUTEUR_LIA ? "Lia" : par === AUTEUR_SYSTEME ? "le système" : null));
  return texteDesChangementsDuSuivi(
    changements.map((c) => changementDuSuivi(c, catalogue, contexte)),
    qui,
    totalAnnonce(texte),
  );
}

/**
 * UN MESSAGE D'UN FIL EN MOTS, pour qui montre le suivi d'une fiche comme un
 * fil (le centre de communication, l'app Canaux) : un evenement systeme voit
 * son texte recompose par `texteDuMessageDuSuivi`, les personnes nommees par
 * l'annuaire de qui le montre (`noms`, par personId) ; un message ecrit par une
 * personne ne change pas. Rendu TEL QUEL (meme objet) quand le texte ne bouge
 * pas : un message neuf, deja en mots, ne coute aucune copie.
 *
 * ICI, ET PAS DANS LE SHELL : le centre (`@taki/shell`) et Canaux (une app, qui
 * ne lit pas le shell) l'appellent tous les deux ; une seconde copie dans
 * l'app serait une seconde regle, qui divergerait.
 */
export function messageDuSuiviEnMots<M extends { auteur?: string | undefined; texte?: string | undefined; systeme?: unknown }>(
  message: M,
  catalogue: CatalogueDuSuivi,
  noms: ReadonlyMap<string, string>,
): M {
  if (message.auteur !== AUTEUR_SYSTEME) return message;
  const nom = (personId: string): string | undefined => noms.get(personId);
  const texte = texteDuMessageDuSuivi(message, catalogue, { nomDePersonne: nom, nomDeQui: nom });
  return texte === message.texte ? message : { ...message, texte };
}
